import { useLazyQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { validate as validUUID } from 'uuid';

import {
  Button,
  Caption,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Heading,
  Icon,
  IconProps,
  LoadingIcon,
  Stack,
  Tag,
  Text,
} from '@eluve/components';
import { ExternalArtifactTypesLookup } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin } from '@eluve/session-helpers';

import { DebugJsonRenderer, getCitationRenderer } from './CitationRenderers';

// Base citation interface for shared components
export interface BaseCitation {
  documentTitle: string;
  citedText: string;
  artifactId?: string;
}

// Document type configuration lookup
export const DOCUMENT_TYPE_CONFIG: Array<{
  keyword: string;
  label: string;
  icon: IconProps['name'];
  color:
    | 'lightblue'
    | 'purple'
    | 'teal'
    | 'orange'
    | 'lightgreen'
    | 'yellow'
    | 'info'
    | 'gray';
}> = [
  {
    keyword: 'lab',
    label: 'Lab Results',
    icon: 'FlaskConical',
    color: 'lightblue',
  },
  { keyword: 'medication', label: 'Meds', icon: 'Pill', color: 'purple' },
  { keyword: 'intake', label: 'Intake', icon: 'FileText', color: 'teal' },
  {
    keyword: 'transcript',
    label: 'Transcript',
    icon: 'FileText',
    color: 'orange',
  },
  {
    keyword: 'patient detail',
    label: 'Patient',
    icon: 'FileText',
    color: 'lightgreen',
  },
  { keyword: 'medical note', label: 'Note', icon: 'FileText', color: 'yellow' },
  {
    keyword: 'condition',
    label: 'Conditions',
    icon: 'Stethoscope',
    color: 'info',
  },
  {
    keyword: 'overview',
    label: 'Overview',
    icon: 'FileText',
    color: 'lightgreen',
  },
  {
    keyword: 'previous rx',
    label: 'Previous Rx',
    icon: 'Pill',
    color: 'purple',
  },
  { keyword: 'allergy', label: 'Allergy', icon: 'FileText', color: 'yellow' },
  {
    keyword: 'clinical note',
    label: 'Note',
    icon: 'FileText',
    color: 'yellow',
  },
  { keyword: 'temp', label: 'Source', icon: 'FileText', color: 'yellow' },
];

// Helper to get document type info (label, icon, and color)
export const getDocumentTypeInfo = (documentTitle: string) => {
  const title = documentTitle.toLowerCase();
  const match = DOCUMENT_TYPE_CONFIG.find((config) =>
    title.includes(config.keyword),
  );
  return (
    match ?? {
      label: 'Source',
      icon: 'FileText' as const,
      color: 'gray' as const,
    }
  );
};

// Map document title keywords to source types for the renderer
const getSourceTypeFromDocumentTitle = (documentTitle: string): string => {
  const title = documentTitle.toLowerCase();
  if (title.includes('lab')) return 'labResults';
  if (title.includes('medication') || title.includes('rx'))
    return 'medicationRequests';
  if (title.includes('condition')) return 'patientConditions';
  if (title.includes('patient detail')) return 'patientDetails';
  if (title.includes('observation')) return 'observations';
  if (title.includes('care plan')) return 'carePlans';
  if (title.includes('note') || title.includes('chart')) return 'signedNotes';
  if (title.includes('intake')) return 'intakeForm';
  if (title.includes('transcript')) return 'signedNotes';
  if (title.includes('allergy')) return 'patientConditions';
  if (title.includes('overview')) return 'patientDetails';
  return 'generic';
};

// Try to parse cited text as JSON
const parseCitedText = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

// Type definition for lab results data structure
type LabResultsData = {
  lab_results?: Array<{ date?: string; tests?: unknown[] }>;
};

// Type guard to check if rawData is LabResultsData
const isLabResultsData = (data: unknown): data is LabResultsData => {
  return typeof data === 'object' && data !== null && 'lab_results' in data;
};

// Extract the specific portion of the artifact that contains the cited text
const getRelevantDataPortion = (
  artifact: {
    rawData: unknown;
    externalArtifactType: string;
  },
  citedText: string,
): unknown => {
  const citedTextLower = citedText.toLowerCase();
  const rawData = artifact.rawData;

  // For lab results, try to find the specific lab result entry
  if (
    artifact.externalArtifactType === ExternalArtifactTypesLookup.LAB_RESULTS &&
    isLabResultsData(rawData)
  ) {
    if (rawData.lab_results && Array.isArray(rawData.lab_results)) {
      // Extract key terms from cited text (test names, values, units)
      // This helps match the exact test result being referenced
      const citedWords = citedTextLower
        .split(/[\s,()]+/)
        .filter((word) => word.length > 0);

      // Score each lab entry based on how many cited terms it contains
      const scoredEntries = rawData.lab_results.map((entry) => {
        const entryString = JSON.stringify(entry).toLowerCase();
        const matchCount = citedWords.filter((word) =>
          entryString.includes(word),
        ).length;
        return { entry, matchCount, entryString };
      });

      // Sort by match count (highest first) and return the best match
      scoredEntries.sort((a, b) => b.matchCount - a.matchCount);

      // Only return if we have a good match (at least 50% of cited words found)
      const bestMatch = scoredEntries[0];
      if (bestMatch && bestMatch.matchCount >= citedWords.length * 0.5) {
        return bestMatch.entry;
      }
    }
  }

  // For medications or if no specific entry found, return the full rawData
  return rawData;
};

// GraphQL query to fetch a specific artifact by ID
const getArtifactByIdQuery = graphql(`
  query getExternalPatientArtifactByIdShared(
    $tenantId: uuid!
    $artifactId: uuid!
  ) {
    externalPatientArtifacts(
      where: { tenantId: { _eq: $tenantId }, id: { _eq: $artifactId } }
    ) {
      id
      name
      rawData
      externalArtifactType
      externalArtifactCreatedAt
    }
  }
`);

// Citation Modal Component
export const CitationDetailsModal: React.FC<{
  citation: BaseCitation;
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}> = ({ citation, isOpen, onClose, tenantId }) => {
  const isEluveAdmin = useIsEluveAdmin();
  const [
    fetchArtifactById,
    { data: artifactByIdData, loading: loadingById, error: errorById },
  ] = useLazyQuery(getArtifactByIdQuery);

  const documentTitle = citation.documentTitle.toLowerCase();
  const shouldFetchArtifact =
    documentTitle.includes('lab') || documentTitle.includes('intake');

  // Check if citation has a valid UUID artifact_id
  const hasValidArtifactId =
    citation.artifactId &&
    typeof citation.artifactId === 'string' &&
    validUUID(citation.artifactId);

  useEffect(() => {
    if (isOpen && shouldFetchArtifact && citation.artifactId) {
      fetchArtifactById({
        variables: {
          tenantId,
          artifactId: citation.artifactId,
        },
      });
    }
  }, [isOpen, shouldFetchArtifact, tenantId, fetchArtifactById, citation]);

  // Merge results from both queries
  const artifacts = artifactByIdData?.externalPatientArtifacts || [];

  const isLoading = hasValidArtifactId ? loadingById : false;
  const fetchError = hasValidArtifactId ? errorById : null;

  // Find artifact containing the cited text
  const relevantArtifact = hasValidArtifactId
    ? artifacts[0] // When fetched by ID, just use the first result
    : artifacts.find((artifact) => {
        // Fallback: search through artifacts
        const rawDataString = JSON.stringify(artifact.rawData).toLowerCase();
        return rawDataString.includes(citation.citedText.toLowerCase());
      });

  const displayData = relevantArtifact
    ? getRelevantDataPortion(relevantArtifact, citation.citedText)
    : null;

  // Parse cited text as JSON for rendering
  const parsedCitedText = parseCitedText(citation.citedText);
  const sourceType = getSourceTypeFromDocumentTitle(citation.documentTitle);
  const { icon } = getDocumentTypeInfo(citation.documentTitle);

  // Determine what data to render with the pretty renderer
  const dataToRender = displayData ?? parsedCitedText;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="4xl" className="flex max-h-[80vh] flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <Icon name={icon} size="xl" className="text-text-secondary" />
            <Heading size="2xl" weight="semibold">
              {citation.documentTitle || 'Source'}
            </Heading>
          </DialogTitle>
        </DialogHeader>

        <Stack gap={4} className="min-h-0 flex-1 overflow-y-auto">
          {/* Artifact metadata bar */}
          {relevantArtifact && (
            <Stack
              gap={4}
              className="rounded-lg bg-bg-gray-subtle px-4 py-2"
              direction="horizontal"
              wrap
            >
              <Stack gap={2} direction="horizontal">
                <Icon name="FileText" className="text-text-tertiary" />
                <Text inline size="xs" color="secondary">
                  {relevantArtifact.name || 'Unnamed Artifact'}
                </Text>
              </Stack>
              {relevantArtifact.externalArtifactCreatedAt && (
                <Stack gap={2} direction="horizontal">
                  <Icon name="Calendar" className="text-text-tertiary" />
                  <Text inline size="xs" color="tertiary">
                    {new Date(
                      relevantArtifact.externalArtifactCreatedAt,
                    ).toLocaleDateString()}
                  </Text>
                </Stack>
              )}
            </Stack>
          )}

          {isLoading && (
            <Stack
              className="py-8"
              direction="horizontal"
              align="center"
              justify="center"
              fullWidth={false}
            >
              <LoadingIcon size="2xl" />
              <Text color="secondary" className="ml-2">
                Loading artifact...
              </Text>
            </Stack>
          )}

          {fetchError && (
            <div className="rounded-lg border bg-bg-negative-subtle p-4">
              <Text color="secondary">
                Error loading artifacts: {fetchError.message}
              </Text>
            </div>
          )}

          {!isLoading &&
            !fetchError &&
            shouldFetchArtifact &&
            !relevantArtifact && (
              <div className="rounded-lg border bg-bg-gray-subtle p-4">
                <Stack gap={2}>
                  <Text color="secondary">
                    Could not find the source document containing this citation.
                  </Text>
                  <Caption size="3xs" color="tertiary">
                    Fetched {artifacts.length} artifact(s).
                  </Caption>
                </Stack>
              </div>
            )}

          {/* Rendered content */}
          {!isLoading && dataToRender != null ? (
            <Stack gap={3}>
              {getCitationRenderer(sourceType, dataToRender)}
            </Stack>
          ) : null}

          {/* Fallback: show plain cited text if no JSON data */}
          {!isLoading && !dataToRender && citation.citedText && (
            <Stack gap={3}>
              <div className="rounded-lg border bg-bg-gray-subtle p-4">
                <Text className="whitespace-pre-wrap">
                  {citation.citedText}
                </Text>
              </div>
            </Stack>
          )}

          {/* Debug JSON for admins */}
          {isEluveAdmin && dataToRender != null ? (
            <Stack gap={2} className="border-t border-border-primary pt-4">
              <Caption size="3xs" color="tertiary">
                ADMIN DEBUG
              </Caption>
              <DebugJsonRenderer data={dataToRender} />
            </Stack>
          ) : null}
        </Stack>

        <Stack
          className="shrink-0 border-t pt-4"
          gap={2}
          direction="horizontal"
          justify="end"
        >
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

// Clickable Citation Tag Component
export const CitationTag: React.FC<{
  citation: BaseCitation;
  tenantId: string | null;
}> = ({ citation, tenantId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { label, color } = getDocumentTypeInfo(citation.documentTitle);

  return (
    <>
      <span
        className="ml-1 inline-block cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsModalOpen(true);
        }}
      >
        <Tag label={label} color={color} size="xs" subtle={true} />
      </span>

      {isModalOpen && tenantId && (
        <CitationDetailsModal
          citation={citation}
          isOpen={true}
          onClose={() => setIsModalOpen(false)}
          tenantId={tenantId}
        />
      )}
    </>
  );
};
